import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Select, Text } from 'rizzui';
import ConfettiAnimation from '../../../../../components/animations/ConfettiAnimation';
import WidgetCard from '../../../../../components/card/WidgetCard';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import Page from '../../../../../layout/Page/Page';
import { UserService } from '../../../../../services/user/userService';
import { UserRankingsApiResponse } from '../../../../../type/entities/ranking-type';
import UserProfileLayout from '../../UserProfileLayout';
import RankingCard from './RankingCard';
import RankingClassificationModal from './RankingClassificationModal';

interface UserRankingsProps { }

const viewModeOptions = [
    { label: 'Rankings Diarios', value: 'daily' },
    { label: 'Rankings Semanales', value: 'weekly' },
    { label: 'Rankings Mensuales', value: 'monthly' },
    { label: 'Rankings Personalizados', value: 'custom' },
];

const UserRankings: React.FC<UserRankingsProps> = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters } = useFilters({ user: id, view_mode: 'monthly' });

    const [selectedRankingId, setSelectedRankingId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [hasShownConfetti, setHasShownConfetti] = useState(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await (new UserService()).showUserRankings(filters);
        return response.getResponseData() as UserRankingsApiResponse;
    }, [filters]));

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES Función para obtener los rankings filtrados según el view_mode actual.
     * @EN Function to get filtered rankings based on current view_mode.
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const getFilteredRankings = useCallback(() => {
        if (!data?.data) return [];
        
        const currentViewMode = filters.filter_filters?.view_mode;
        
        // Si no hay filtro seleccionado, mostrar todos los rankings
        if (!currentViewMode) return data.data;
        
        return data.data.filter((ranking: any) => {
            return ranking.rankingResetInterval === currentViewMode;
        });
    }, [data, filters.filter_filters?.view_mode]);
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES Función para determinar si el usuario tiene el primer puesto en algún ranking visible.
     * @EN Function to determine if the user has first place in any visible ranking.
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const hasFirstPlaceInVisibleRankings = useCallback(() => {
        const filteredRankings = getFilteredRankings();
        return filteredRankings.some((ranking: any) => ranking.position === 1);
    }, [getFilteredRankings]);
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES Función para manejar el clic en una tarjeta de ranking y abrir el modal correspondiente.
     * @EN Function to handle clicking on a ranking card and opening the corresponding modal.
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleRankingClick = (rankingId: string) => {
        setSelectedRankingId(rankingId);
        setIsModalOpen(true);
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES Función para manejar el cierre del modal de clasificación del ranking.
     * @EN Function to handle closing the ranking classification modal.
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRankingId(null);
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES Función para manejar la finalización de la animación de confeti.
     * @EN Function to handle the completion of the confetti animation.
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleConfettiComplete = () => {
        setShowConfetti(false);
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    // Reset confetti state when filters change
    useEffect(() => {
        setHasShownConfetti(false);
        setShowConfetti(false);
    }, [filters.filter_filters?.view_mode]);

    // Show confetti when data is loaded and user has first place in visible rankings, but only once
    useEffect(() => {
        if (!loading && !error && data?.data && hasFirstPlaceInVisibleRankings() && !hasShownConfetti) {
            // Delay to allow the page to render before showing confetti
            const timer = setTimeout(() => {
                setShowConfetti(true);
                setHasShownConfetti(true);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [data, loading, error, hasFirstPlaceInVisibleRankings, hasShownConfetti, filters.filter_filters?.view_mode]);

    return (
        <UserProfileLayout isLoading={loading}>
            <Page container="fluid">
                <WidgetCard
                    title={
                        <div className="flex items-center">
                            <div className="flex items-center gap-3">
                                <Text className="text-xl font-semibold text-gray-900">
                                    Rankings
                                </Text>
                            </div>
                            <div className="flex justify-end items-center gap-3 ms-5">
                                <Select
                                    value={{ label: viewModeOptions.find(option => option.value === filters.filter_filters?.view_mode)?.label, value: filters.filter_filters?.view_mode }}
                                    onChange={(e: any) => updateFilters({ view_mode: e.value })}
                                    options={viewModeOptions}
                                    size="sm"
                                    className="min-w-[140px]"
                                    placeholder="Seleccionar vista"
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={refetch}
                                    disabled={loading}
                                    isLoading={loading}
                                >
                                    Actualizar
                                </Button>
                            </div>
                        </div>
                    }
                >
                    <div className="mt-6">
                        {loading && <Loader height='70vh' />}

                        {error && (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <Text className="text-red-600 mb-2">Error al cargar los rankings</Text>
                                    <Button variant="outline" size="sm" onClick={refetch}>
                                        Reintentar
                                    </Button>
                                </div>
                            </div>
                        )}

                        {!loading && !error && data?.data && getFilteredRankings().length === 0 && (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-center">
                                    <Text className="text-gray-500 mb-2">
                                        {(() => {
                                            const currentViewMode = filters.filter_filters?.view_mode;
                                            switch (currentViewMode) {
                                                case 'daily':
                                                    return 'No tienes rankings diarios activos en este momento';
                                                case 'weekly':
                                                    return 'No tienes rankings semanales activos en este momento';
                                                case 'monthly':
                                                    return 'No tienes rankings mensuales activos en este momento';
                                                case 'custom':
                                                    return 'No tienes rankings personalizados activos en este momento';
                                                default:
                                                    return 'No tienes rankings activos en este momento';
                                            }
                                        })()}
                                    </Text>
                                    <Text className="text-sm text-gray-400">
                                        Los rankings aparecerán aquí cuando seas agregado a alguno
                                    </Text>
                                </div>
                            </div>
                        )}

                        {!loading && !error && data?.data && getFilteredRankings().length > 0 && (
                            <>
                                {/* Ranking Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {getFilteredRankings().map((ranking: any) => (
                                        <RankingCard
                                            key={ranking.rankingId}
                                            rankingName={ranking.rankingName}
                                            rankingDescription={ranking.rankingDescription}
                                            position={ranking.position}
                                            userNumbers={ranking.userNumbers}
                                            score={ranking.score}
                                            totalScore={ranking.totalScore}
                                            onClick={() => handleRankingClick(ranking.rankingId)}
                                        />
                                    ))}
                                </div>

                                {/* Ranking Statistics */}
                                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                                        <div>
                                            <Text className="text-2xl font-bold text-primary-800">
                                                {getFilteredRankings().filter((ranking: any) => ranking.rankingActive).length} ({getFilteredRankings().length})
                                            </Text>
                                            <Text className="text-sm text-gray-600">Rankings Activos</Text>
                                        </div>
                                        <div>
                                            <Text className="text-2xl font-bold text-primary-800">
                                                {getFilteredRankings().reduce((acc: number, ranking: any) => acc + (ranking.score || 0), 0).toFixed(2)}
                                            </Text>
                                            <Text className="text-sm text-gray-600">Puntos Totales</Text>
                                        </div>
                                        <div>
                                            <Text className="text-2xl font-bold text-primary-800">
                                                {getFilteredRankings().length > 0 ? Math.round(
                                                    getFilteredRankings().reduce((acc: number, ranking: any) => {
                                                        const percentage = ranking.userNumbers > 0
                                                            ? ((ranking.userNumbers - ranking.position + 1) / ranking.userNumbers) * 100
                                                            : 0;
                                                        return acc + percentage;
                                                    }, 0) / getFilteredRankings().length
                                                ) : 0}%
                                            </Text>
                                            <Text className="text-sm text-gray-600">Rendimiento Promedio</Text>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </WidgetCard>

                {/* Ranking Classification Modal */}
                {isModalOpen && selectedRankingId && (
                    <RankingClassificationModal
                        rankingId={selectedRankingId}
                        rankingTitle={data?.data?.find((ranking: any) => ranking.rankingId === selectedRankingId)?.rankingName || 'Ranking'}
                        isOpen={isModalOpen}
                        currentUserId={id}
                        onClose={handleCloseModal}
                    />
                )}

                {/* Confetti Animation */}
                <ConfettiAnimation
                    isActive={showConfetti}
                    duration={4000}
                    particleCount={60}
                    onComplete={handleConfettiComplete}
                />
            </Page>
        </UserProfileLayout>
    );
};

export default UserRankings;